import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { Row as ColumnRow } from 'primereact/row'
import { FaFileExcel, FaFilePdf } from 'react-icons/fa'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import XLSX from 'xlsx-js-style'

import useFetchData from '../../hooks/UseFetchData'
import CustomButton from '../../shared/CustomButton'
import CustomTable from '../../shared/CustomTable'
import DatePicker from '../../shared/DatePicker'
import DropdownField from '../../shared/DropdownField'
import { fetchData } from '../../helpers/fetchData'
import toast from 'react-hot-toast'
import { setList } from '../../redux/slices/AppSlice'
import { AmiriRegBase64 } from '../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../assets/fonts/AmiriB'
import {
  REPORT_MODES,
  reportModeOptions,
  splitSectionsByUser,
} from '../../utils/UserCollectionReportModes'

const amountFields = [
  'patient_share',
  'patient_vat',
  'net_patient_payable',
  'company_share',
  'company_vat',
  'net_company_payable',
  'discount',
  'vat',
  'gross',
  'net_with_vat',
  'net_without_vat',
  'total_net',
]

const amount = value => Number.parseFloat(value || 0)
const money = value => amount(value).toFixed(2)

const userName = row => {
  if (row?.name && row?.created_by && String(row.name) === String(row.created_by)) return row.name
  if (row?.name && row?.created_by) return `${row.name} (${row.created_by})`
  return row?.name || row?.created_by || '-'
}

const sumRows = rows =>
  amountFields.reduce(
    (total, field) => ({
      ...total,
      [field]: rows.reduce((sum, r) => sum + amount(r?.[field]), 0),
    }),
    {}
  )

const flattenGroupedReports = data => {
  const rows = []

  ;(data?.grouped_reports || []).forEach(section => {
    rows.push({
      isGroupHeader: true,
      group_name: section?.group_name,
      name: section?.group_name,
    })

    ;(section?.rows || []).forEach(row => {
      rows.push({
        ...row,
        group_name: section?.group_name,
      })
    })

    rows.push({
      ...(section?.total || {}),
      isSubtotal: true,
      group_name: section?.group_name,
      name: `${section?.group_name} Total`,
    })
  })

  if (data?.grand_total) {
    rows.push({
      ...data.grand_total,
      isGrandTotal: true,
      name: 'Grand Total',
    })
  }

  return rows
}

const totalStyle = {
  backgroundColor: '#4B5563',
  color: '#FFFFFF',
  fontWeight: 'bold',
}

const subtotalStyle = {
  backgroundColor: '#E5E7EB',
  color: '#111827',
  fontWeight: 'bold',
}

const groupHeaderStyle = {
  backgroundColor: '#D1D5DB',
  color: '#111827',
  fontWeight: 'bold',
  borderTop: '2px solid #9CA3AF',
  borderBottom: '2px solid #9CA3AF',
  paddingTop: '0.65rem',
  paddingBottom: '0.65rem',
}

const rowStyle = {
  backgroundColor: '#F3F4F6',
}

const numericCell = field => row => {
  if (row?.isGroupHeader) return ''
  return <span className={row?.isSubtotal || row?.isGrandTotal ? 'font-bold' : ''}>{money(row?.[field])}</span>
}

const cellStyle = row => {
  if (row?.isGrandTotal) return totalStyle
  if (row?.isSubtotal) return subtotalStyle
  if (row?.isGroupHeader) return groupHeaderStyle
  return rowStyle
}

const columns = t => [
  {
    header: t('User Name'),
    bodyCellClass: 'border !border-gray-300',
    body: row => {
      if (row?.isGroupHeader) {
        return <span className="font-bold text-[15px] uppercase tracking-wide">{t(row?.group_name)}</span>
      }
      if (row?.isSubtotal) {
        return <span className="font-bold">{t(row?.name)}</span>
      }
      if (row?.isGrandTotal) {
        return <span className="font-bold text-lg">{t('Grand Total')}</span>
      }
      return userName(row)
    },
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Patient Share'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('patient_share'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Patient VAT'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('patient_vat'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Patient Total'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('net_patient_payable'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Company Share'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('company_share'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Company VAT'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('company_vat'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Company Total'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('net_company_payable'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Discount'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('discount'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('VAT'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('vat'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Gross'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('gross'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Net with VAT'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('net_with_vat'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Net w/o VAT'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('net_without_vat'),
    bodyCellStyle: cellStyle,
  },
  {
    header: t('Net'),
    bodyCellClass: 'border !border-gray-300',
    body: numericCell('total_net'),
    bodyCellStyle: cellStyle,
  },
]

const buildHeaderGroup = t => (
  <ColumnGroup>
    <ColumnRow style={{ alignItems: 'end', borderWidth: 1 }}>
      <Column header={t('User Name')} rowSpan={2} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('Patient')} colSpan={3} align="center" />
      <Column header={t('Company')} colSpan={3} align="center" />
      <Column header={t('Discount')} rowSpan={2} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('VAT')} rowSpan={2} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('Gross')} rowSpan={2} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('Net with VAT')} rowSpan={2} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('Net w/o VAT')} rowSpan={2} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('Net')} rowSpan={2} style={{ paddingLeft: '0.5rem' }} />
    </ColumnRow>
    <ColumnRow>
      <Column header={t('Share')} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('VAT')} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('Total')} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('Share')} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('VAT')} style={{ paddingLeft: '0.5rem' }} />
      <Column header={t('Total')} style={{ paddingLeft: '0.5rem' }} />
    </ColumnRow>
  </ColumnGroup>
)

const buildGroupedWorksheet = (rows, t) => {
  if (!rows?.length) return

  const headers = [
    t('User Name'),
    t('Patient Share'),
    t('Patient VAT'),
    t('Patient Total'),
    t('Company Share'),
    t('Company VAT'),
    t('Company Total'),
    t('Discount'),
    t('VAT'),
    t('Gross'),
    t('Net with VAT'),
    t('Net w/o VAT'),
    t('Net'),
  ]

  const formatted = rows.map(row => {
    if (row?.isGroupHeader) {
      return headers.reduce(
        (acc, header, index) => ({
          ...acc,
          [header]: index === 0 ? t(row?.group_name) : '',
        }),
        {}
      )
    }

    return {
      [t('User Name')]: row?.isGrandTotal ? t('Grand Total') : row?.isSubtotal ? t(row?.name) : userName(row),
      [t('Patient Share')]: money(row?.patient_share),
      [t('Patient VAT')]: money(row?.patient_vat),
      [t('Patient Total')]: money(row?.net_patient_payable),
      [t('Company Share')]: money(row?.company_share),
      [t('Company VAT')]: money(row?.company_vat),
      [t('Company Total')]: money(row?.net_company_payable),
      [t('Discount')]: money(row?.discount),
      [t('VAT')]: money(row?.vat),
      [t('Gross')]: money(row?.gross),
      [t('Net with VAT')]: money(row?.net_with_vat),
      [t('Net w/o VAT')]: money(row?.net_without_vat),
      [t('Net')]: money(row?.total_net),
    }
  })

  const worksheet = XLSX.utils.json_to_sheet(formatted, { header: headers })
  const range = XLSX.utils.decode_range(worksheet['!ref'])
  const headerStyle = {
    fill: { patternType: 'solid', fgColor: { rgb: 'D1D5DB' } },
    font: { color: { rgb: '111827' }, bold: true, sz: 10, name: 'Calibri' },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    },
  }
  const rowStyle = (fill, isBold = false, fontColor = '000000') => ({
    fill: { patternType: 'solid', fgColor: { rgb: fill } },
    font: { bold: isBold, color: { rgb: fontColor }, sz: 9, name: 'Calibri' },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'hair', color: { rgb: 'CCCCCC' } },
      bottom: { style: 'hair', color: { rgb: 'CCCCCC' } },
      left: { style: 'hair', color: { rgb: 'CCCCCC' } },
      right: { style: 'hair', color: { rgb: 'CCCCCC' } },
    },
  })

  for (let C = range.s.c; C <= range.e.c; ++C) {
    const headerAddress = XLSX.utils.encode_col(C) + '1'
    if (worksheet[headerAddress]) worksheet[headerAddress].s = headerStyle

    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      const row = rows[R - 1]
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C })
      if (!worksheet[cellAddress]) worksheet[cellAddress] = { v: '', t: 's' }
      const fill = row?.isGrandTotal ? '4B5563' : row?.isSubtotal ? 'E5E7EB' : row?.isGroupHeader ? 'D1D5DB' : 'F3F4F6'
      const isBold = row?.isGrandTotal || row?.isSubtotal || row?.isGroupHeader
      const fontColor = row?.isGrandTotal ? 'FFFFFF' : row?.isGroupHeader ? '111827' : row?.isSubtotal ? '111827' : '000000'
      worksheet[cellAddress].s = rowStyle(fill, isBold, fontColor)
    }
  }

  worksheet['!cols'] = new Array(Object.keys(formatted[0] || {}).length).fill({ wch: 18 })
  return worksheet
}

const exportGroupedToExcel = (rows, t) => {
  if (!rows?.length) return
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, buildGroupedWorksheet(rows, t), 'Grouped Collection')
  XLSX.writeFile(workbook, 'user-collection-grouped-report.xlsx')
}

const appendGroupedSheets = (workbook, userBlocks, rows, t) => {
  userBlocks.forEach(block => {
    const sheetName = block.sheetName?.slice?.(0, 31) || 'Individual'
    XLSX.utils.book_append_sheet(workbook, buildGroupedWorksheet(block.rows, t), sheetName)
  })
  if (rows?.length) {
    XLSX.utils.book_append_sheet(workbook, buildGroupedWorksheet(rows, t), 'Combined')
  }
}

const exportGroupedIndividualToExcel = (userBlocks, rows, t) => {
  if (!userBlocks?.length && !rows?.length) return
  const workbook = XLSX.utils.book_new()
  appendGroupedSheets(workbook, userBlocks, rows, t)
  XLSX.writeFile(workbook, 'user-collection-grouped-report.xlsx')
}

const drawGroupedPdfHeader = (doc, t, payloadData, auth) => {
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 5
  const reportTitle = t('User Collection Grouped Report')
  const orgName =
    auth?.buisnessInfo?.name_en ||
    auth?.buisnessInfo?.name_ar ||
    t('Organization')
  const location = auth?.buisnessInfo?.address || ''
  const logo = auth?.buisnessInfo?.logo
  const currentDate = moment().format('DD/MM/YYYY HH:mm')
  const startDate = moment(payloadData?.start_date).format('DD/MM/YYYY')
  const endDate = moment(payloadData?.end_date).format('DD/MM/YYYY')
  const selectedUser =
    payloadData?.created_by ||
    t('All Users')

  const logoSrc = logo?.startsWith?.('data:')
    ? logo
    : logo
      ? `data:image/png;base64,${logo}`
      : ''

  doc.setFillColor(55, 65, 81)
  doc.rect(0, 0, pageWidth, 24, 'F')
  if (logoSrc) {
    try {
      doc.addImage(logoSrc, 'PNG', margin, 2, 16, 16)
    } catch (_) {}
  }
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(10)
  doc.setFont('Amiri', 'bold')
  doc.text(orgName, margin + 20, 9)
  doc.setFontSize(7)
  doc.setFont('Amiri', 'normal')
  if (location) doc.text(location, margin + 20, 15)
  doc.setFontSize(11)
  doc.setFont('Amiri', 'bold')
  doc.text(reportTitle, pageWidth - margin, 9, { align: 'right' })
  doc.setFontSize(7)
  doc.setFont('Amiri', 'normal')
  doc.text(`${t('Printed On')}: ${currentDate}`, pageWidth - margin, 15, {
    align: 'right',
  })

  doc.setTextColor(50, 50, 50)
  doc.setFontSize(7)
  doc.setFont('Amiri', 'normal')
  doc.text(`${t('Period')}: ${startDate} - ${endDate}`, margin, 29)
  doc.text(`${t('Report For')}: ${selectedUser}`, pageWidth - margin, 29, {
    align: 'right',
  })
  doc.setDrawColor(200, 200, 200)
  doc.setLineWidth(0.3)
  doc.line(margin, 31.5, pageWidth - margin, 31.5)

  return 35
}

const buildGroupedPdfBody = (rows, t) =>
  rows.map(row => {
    if (row?.isGroupHeader) {
      return [t(row?.group_name), ...amountFields.map(() => '')]
    }
    return [
      row?.isGrandTotal ? t('Grand Total') : row?.isSubtotal ? t(row?.name) : userName(row),
      money(row?.patient_share),
      money(row?.patient_vat),
      money(row?.net_patient_payable),
      money(row?.company_share),
      money(row?.company_vat),
      money(row?.net_company_payable),
      money(row?.discount),
      money(row?.vat),
      money(row?.gross),
      money(row?.net_with_vat),
      money(row?.net_without_vat),
      money(row?.total_net),
    ]
  })

const drawGroupedPdfTable = (doc, rows, t, startY) => {
  autoTable(doc, {
    startY,
    head: [[
      t('User Name'),
      t('Patient Share'),
      t('Patient VAT'),
      t('Patient Total'),
      t('Company Share'),
      t('Company VAT'),
      t('Company Total'),
      t('Discount'),
      t('VAT'),
      t('Gross'),
      t('Net with VAT'),
      t('Net w/o VAT'),
      t('Net'),
    ]],
    body: buildGroupedPdfBody(rows, t),
    styles: { font: 'Amiri', fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [209, 213, 219], textColor: [17, 24, 39], fontStyle: 'bold' },
    didParseCell: data => {
      const row = rows[data.row.index]
      if (data.section !== 'body') return
      if (row?.isGrandTotal) {
        data.cell.styles.fillColor = [55, 65, 81]
        data.cell.styles.textColor = 255
        data.cell.styles.fontStyle = 'bold'
      } else if (row?.isSubtotal) {
        data.cell.styles.fillColor = [229, 231, 235]
        data.cell.styles.textColor = [17, 24, 39]
        data.cell.styles.fontStyle = 'bold'
      } else if (row?.isGroupHeader) {
        data.cell.styles.fillColor = [209, 213, 219]
        data.cell.styles.textColor = [17, 24, 39]
        data.cell.styles.fontStyle = 'bold'
      }
    },
  })
  return doc.lastAutoTable.finalY
}

const exportGroupedToPDF = (rows, t, payloadData, auth) => {
  if (!rows?.length) return

  const doc = new jsPDF('landscape')
  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
  doc.setFont('Amiri', 'normal')

  const y = drawGroupedPdfHeader(doc, t, payloadData, auth)
  drawGroupedPdfTable(doc, rows, t, y)

  doc.save('user-collection-grouped-report.pdf')
}

const exportGroupedIndividualToPDF = (userBlocks, rows, t, payloadData, auth) => {
  if (!userBlocks?.length && !rows?.length) return

  const doc = new jsPDF('landscape')
  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
  doc.setFont('Amiri', 'normal')

  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 5
  let y = drawGroupedPdfHeader(doc, t, payloadData, auth)

  userBlocks.forEach(block => {
    doc.setFontSize(10)
    doc.setFont('Amiri', 'bold')
    doc.setTextColor(30, 41, 59)
    doc.text(block.sheetName, margin, y)
    y += 5
    y = drawGroupedPdfTable(doc, block.rows, t, y) + 4
    doc.setDrawColor(200, 200, 200)
    doc.setLineWidth(0.2)
    doc.line(margin, y, pageWidth - margin, y)
  })

  if (rows?.length) {
    y = drawGroupedPdfTable(doc, rows, t, y + 2)
  }

  doc.save('user-collection-grouped-report.pdf')
}

function UserCollectionGroupedReport() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { list } = useSelector(state => state?.app)
  const auth = useSelector(state => state.auth)
  const { data: requestData, executeFetch: requestFetch, loading } = useFetchData()
  const [payloadData, setPayloadData] = useState({
    end_date: moment().toDate(),
    start_date: moment().subtract(1, 'day').toDate(),
    created_by: '',
    report_display_mode: REPORT_MODES.individual,
  })

  const rows = useMemo(() => flattenGroupedReports(requestData), [requestData])

  const userBlocks = useMemo(() => {
    return splitSectionsByUser(requestData?.grouped_reports).flatMap(section =>
      section.users.map(user => ({
        sheetName: user.label,
        rows: [
          ...user.rows.map(row => ({ ...row, group_name: user.label })),
          {
            ...sumRows(user.rows),
            isSubtotal: true,
            group_name: user.label,
            name: t('Total'),
          },
        ],
      }))
    )
  }, [requestData, t])

  const displayMode =
    payloadData?.report_display_mode || REPORT_MODES.combined

  const handleChange = event => {
    const { name, value } = event.target || {}
    setPayloadData(prev => ({ ...prev, [name]: value }))
  }

  const handleModeChange = event => {
    const value = event?.target?.value ?? event?.value
    setPayloadData(prev => ({ ...prev, report_display_mode: value }))
  }

  const requestReport = async (payload = payloadData) => {
    if (moment(payload?.end_date).isAfter(moment(payload?.start_date).add(1, 'month'))) {
      return toast.error(t('Date range cannot exceed 1 month'))
    }

    await requestFetch('POST', '/v3/reports/user-collection-grouped', {
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
      created_by: payload.created_by || '',
    })
  }

  const handleReset = async () => {
    const payload = {
      end_date: moment().toDate(),
      start_date: moment().subtract(1, 'day').toDate(),
      created_by: '',
      report_display_mode: payloadData?.report_display_mode || REPORT_MODES.individual,
    }
    setPayloadData(payload)
    await requestReport(payload)
  }

  useEffect(() => {
    const fetchReceptionists = async () => {
      if (list?.receptionists?.length > 0) return
      try {
        const response = await fetchData('GET', '/v3/invoices/getCreators', {})
        if (response?.data) {
          const receptionists = response.data.map(receptionist => ({
            ...receptionist,
            label: receptionist?.staff_info?.user?.fullname
              ? `${receptionist.staff_info.user.fullname} (${receptionist.created_by})`
              : receptionist.created_by,
            employee_id: receptionist.created_by,
          }))
          dispatch(setList({ key: 'receptionists', value: receptionists }))
        }
      } catch (error) {
        console.error('Error fetching receptionists:', error)
      }
    }

    fetchReceptionists()
  }, [dispatch, list?.receptionists])

  return (
    <div>
      <div className="flex items-center w-full justify-between gap-2 mb-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <DatePicker
              name="start_date"
              useFormik={false}
              className="mt-1"
              value={payloadData?.start_date}
              onChange={handleChange}
              placeholder={t('Selectstartdate')}
              format="YYYY-MM-DD"
            />
            <DatePicker
              name="end_date"
              useFormik={false}
              className="mt-1"
              value={payloadData?.end_date}
              onChange={handleChange}
              placeholder={t('Selectenddate')}
              format="YYYY-MM-DD"
              minDate={payloadData?.start_date ? moment(payloadData.start_date).toDate() : null}
              maxDate={payloadData?.start_date ? moment(payloadData.start_date).add(1, 'month').toDate() : null}
            />
            <DropdownField
              parentClass="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-2"
              placeholder={`${t('Select')} ${t('receptionist')}`}
              className="!mb-0"
              name="created_by"
              filter
              useFormik={false}
              options={list?.receptionists || []}
              optionLabel="label"
              optionValue="employee_id"
              value={payloadData?.created_by}
              onChange={handleChange}
            />
          </div>
          <div className="flex items-center gap-2">
            <CustomButton
              title={t('ApplyFilters')}
              buttonType="primary"
              icon="pi pi-filter"
              className="w-fit h-[35px]"
              onClick={() => requestReport(payloadData)}
            />
            <CustomButton
              title={t('Reset')}
              buttonType="reset"
              icon="pi pi-sync"
              className="w-fit h-[35px]"
              onClick={handleReset}
            />
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <DropdownField
            placeholder={t('Show Reports')}
            className="!mb-0 !w-[180px] !h-[35px] justify-center"
            name="report_display_mode"
            useFormik={false}
            options={reportModeOptions(t)}
            optionLabel="label"
            optionValue="value"
            value={displayMode}
            onChange={handleModeChange}
          />
          <CustomButton
            title={t('Export to Excel')}
            buttonType="success"
            icon={FaFileExcel}
            className="w-fit h-[35px]"
            onClick={() =>
              displayMode === REPORT_MODES.combined
                ? exportGroupedToExcel(rows, t)
                : exportGroupedIndividualToExcel(
                    userBlocks,
                    displayMode === REPORT_MODES.both ? rows : [],
                    t
                  )
            }
          />
          <CustomButton
            title={t('Export to PDF')}
            buttonType="danger"
            icon={FaFilePdf}
            className="w-fit h-[35px]"
            onClick={() =>
              displayMode === REPORT_MODES.combined
                ? exportGroupedToPDF(rows, t, payloadData, auth)
                : exportGroupedIndividualToPDF(
                    userBlocks,
                    displayMode === REPORT_MODES.both ? rows : [],
                    t,
                    payloadData,
                    auth
                  )
            }
          />
        </div>
      </div>

      {displayMode === REPORT_MODES.combined ? (
        <CustomTable
          data={rows}
          isLoading={loading}
          columns={columns(t)}
          headerColumnGroup={buildHeaderGroup(t)}
          rowClassName={row => {
            if (row?.isGroupHeader) return '!bg-white'
            if (row?.isSubtotal) return '!bg-gray-200'
            if (row?.isGrandTotal) return '!bg-white'
          }}
          showGridlines
        />
      ) : (
        <div className="space-y-4">
          {userBlocks.map((block, index) => (
            <div key={index}>
              <div className="bg-gray-300 text-gray-800 font-bold text-[14px] uppercase tracking-wide px-4 py-2 rounded-t">
                {block.sheetName}
              </div>
              <CustomTable
                data={block.rows}
                isLoading={loading}
                columns={columns(t)}
                headerColumnGroup={buildHeaderGroup(t)}
                rowClassName={row => {
                  if (row?.isGroupHeader) return '!bg-white'
                  if (row?.isSubtotal) return '!bg-gray-200'
                  if (row?.isGrandTotal) return '!bg-white'
                }}
                showGridlines
              />
            </div>
          ))}
          {displayMode === REPORT_MODES.both && rows?.length > 0 && (
            <div>
              <div className="bg-gray-300 text-gray-800 font-bold text-[14px] uppercase tracking-wide px-4 py-2 rounded-t">
                {t('Combined')}
              </div>
              <CustomTable
                data={rows}
                isLoading={loading}
                columns={columns(t)}
                headerColumnGroup={buildHeaderGroup(t)}
                rowClassName={row => {
                  if (row?.isGroupHeader) return '!bg-white'
                  if (row?.isSubtotal) return '!bg-gray-200'
                  if (row?.isGrandTotal) return '!bg-white'
                }}
                showGridlines
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default UserCollectionGroupedReport
